import { Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

interface BaseChatPanelProps {
  /** Header element. Pass `<BaseChatPanelHeader />` or any custom node; omit to render no header. */
  header?: ReactNode | undefined;
  /** Scrollable message-area content (bubbles, status cards, day dividers, etc.). */
  children: ReactNode;
  /** Optional content rendered between messages and input — typically suggestions or quick-action chips. */
  belowMessages?: ReactNode | undefined;
  /** Input element such as `<ChatInput />`. Omit to render a readonly panel. */
  input?: ReactNode | undefined;
  /** Extra classes for the outer container — e.g. `border-r` for left-side-panel layouts. */
  className?: string | undefined;
  /** Optional ref to the scrollable messages container — lets panels drive auto-scroll (e.g. LLM streaming). */
  scrollContainerRef?: React.Ref<HTMLDivElement> | undefined;
}

/**
 * Layout shell shared by all chat panels — fixed header / scrollable messages /
 * optional below-messages slot / optional input. Used by both the create-experiment
 * readonly chat and the experiment-details Lily Agent so styling stays consistent.
 */
export function BaseChatPanel({
  header,
  children,
  belowMessages,
  input,
  className,
  scrollContainerRef,
}: BaseChatPanelProps) {
  return (
    <div className={cn('flex h-full flex-col', className)}>
      {header}
      <div ref={scrollContainerRef} className="flex-1 space-y-4 overflow-y-auto p-4">
        {children}
      </div>
      {belowMessages}
      {input ? <div className="border-t p-3">{input}</div> : null}
    </div>
  );
}

interface BaseChatPanelHeaderProps {
  /** Title displayed next to the icon (e.g. "Lily Copilot", "Lily Agent"). */
  title: string;
  /** Lead icon. Defaults to a Sparkles glyph in the project's accent color. Pass `null` to omit. */
  icon?: ReactNode | undefined;
  /** Right-aligned slot for status indicators or icon-buttons. */
  actions?: ReactNode | undefined;
}

/**
 * Minimal panel header — icon + title (left) and optional actions (right) over a bottom divider.
 * Distinct from the workspace `ChatHeader`, which carries streaming-state dot + clear / close controls.
 */
export function BaseChatPanelHeader({
  title,
  icon = <Sparkles className="text-accent-main h-5 w-5" />,
  actions,
}: BaseChatPanelHeaderProps) {
  return (
    <div className="border-border flex items-center justify-between gap-2 border-b px-4 py-3">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      {actions}
    </div>
  );
}
